import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Payment,
  Today,
  CalendarMonth,
  Print,
  Refresh,
  Search,
  FilterList,
  PictureAsPdf,
  TableChart,
  AccountBalance,
  CreditCard,
  Receipt,
  Visibility,
  ErrorOutline,
  ExpandMore,
  AccountBalanceWallet,
  ShoppingCart,
  Business,
  Person,
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon
} from '@mui/icons-material';
import HttpsIcon from '@mui/icons-material/Https';

import { Input, message, ConfigProvider } from 'antd';
import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import LoadingInformation from '../../Components/LoadingInformation';

moment.locale('es');

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const COLORS = ['#f759ab', '#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4', '#84cc16', '#f59e0b'];

function DailyCashReport() {
  const [payments, setPayments] = useState([]);
  const [operationalTransactions, setOperationalTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState('');
  const [transactionType, setTransactionType] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [permissionError, setPermissionError] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState(['payments', 'operational']);
  const [selectedDate, setSelectedDate] = useState(moment());

  // Summary metrics
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netCashFlow: 0,
    paymentIncome: 0,
    paymentExpenses: 0,
    operationalIncome: 0,
    operationalExpenses: 0,
    transactionsByType: {},
    cashFlowByHour: {},
    topCategories: []
  });

  const navigate = useNavigate();

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    setPermissionError(false);
    setLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const token = localStorage.getItem('token');

  // Navigation functions
  const goToPreviousDay = () => {
    setSelectedDate(prev => prev.clone().subtract(1, 'day'));
  };

  const goToNextDay = () => {
    setSelectedDate(prev => prev.clone().add(1, 'day'));
  };

  const goToToday = () => {
    setSelectedDate(moment());
  };

  const getPayments = async (date = selectedDate) => {
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/invoices/list-today-all-invoices-details/${date.format('YYYY-MM-DD')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.map(payment => ({
          ...payment,
          type: 'payment',
          flow_type: payment.flowType,  // Usar el valor real de flowType
          category_name: payment.flowType === 'ingreso' ? 'Pago de Factura' : 'Reembolso de Factura',
          source: 'Facturación'
        }));
      } else {
        console.error('Error al cargar los pagos');
        return [];
      }
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  };

  const getOperationalTransactions = async (date = selectedDate) => {
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/finances/operational-transactions/daily/${date.format('YYYY-MM-DD')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.map(transaction => ({
          ...transaction,
          type: 'operational',
          // Mapear campos para consistencia
          amount: transaction.amount,
          paymentMethod: transaction.paymentMethod,
          description: transaction.description,
          creationDate: transaction.transaction_date,
          clientName: transaction.supplier_name || transaction.employeeName || 'N/A',
          invoiceNumber: transaction.receipt_reference || `OP-${transaction.transaction_ID}`,
          source: 'Operativo'
        }));
      } else {
        console.error('Error al cargar las transacciones operativas');
        return [];
      }
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [paymentsData, operationalData] = await Promise.all([
        getPayments(),
        getOperationalTransactions()
      ]);

      const allData = [...paymentsData, ...operationalData];
      setPayments(paymentsData);
      setOperationalTransactions(operationalData);
      setAllTransactions(allData);
      setFilteredTransactions(allData);
      calculateSummary(allData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      messageApi.error('Error al cargar los datos del reporte');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (transactionData) => {
    const totalIncome = transactionData
      .filter(t => t.flow_type === 'ingreso')
      .reduce((sum, t) => sum + parseNumber(t.amount), 0);

    const totalExpenses = transactionData
      .filter(t => t.flow_type === 'egreso')
      .reduce((sum, t) => sum + parseNumber(t.amount), 0);

    const netCashFlow = totalIncome - totalExpenses;

    const paymentIncome = payments
      .filter(p => p.flow_type === 'ingreso')
      .reduce((sum, p) => sum + parseNumber(p.amount), 0);

    const paymentExpenses = payments
      .filter(p => p.flow_type === 'egreso')
      .reduce((sum, p) => sum + parseNumber(p.amount), 0);

    const operationalIncome = operationalTransactions
      .filter(t => t.flow_type === 'ingreso')
      .reduce((sum, t) => sum + parseNumber(t.amount), 0);

    const operationalExpenses = operationalTransactions
      .filter(t => t.flow_type === 'egreso')
      .reduce((sum, t) => sum + parseNumber(t.amount), 0);

    // Agrupar por tipo de transacción
    const transactionsByType = {};
    transactionData.forEach(transaction => {
      const type = transaction.type;
      if (!transactionsByType[type]) {
        transactionsByType[type] = { count: 0, amount: 0 };
      }
      transactionsByType[type].count++;
      transactionsByType[type].amount += parseNumber(transaction.amount);
    });

    // Flujo de caja por hora
    const cashFlowByHour = {};
    transactionData.forEach(transaction => {
      const hour = moment(transaction.creationDate).format('HH:00');
      if (!cashFlowByHour[hour]) {
        cashFlowByHour[hour] = { hour, income: 0, expenses: 0, net: 0 };
      }
      if (transaction.flow_type === 'ingreso') {
        cashFlowByHour[hour].income += parseNumber(transaction.amount);
      } else {
        cashFlowByHour[hour].expenses += parseNumber(transaction.amount);
      }
      cashFlowByHour[hour].net = cashFlowByHour[hour].income - cashFlowByHour[hour].expenses;
    });

    // Top categorías
    const categoryTotals = {};
    operationalTransactions.forEach(transaction => {
      const category = transaction.category_name;
      if (!categoryTotals[category]) {
        categoryTotals[category] = { income: 0, expenses: 0 };
      }
      if (transaction.flow_type === 'ingreso') {
        categoryTotals[category].income += parseNumber(transaction.amount);
      } else {
        categoryTotals[category].expenses += parseNumber(transaction.amount);
      }
    });

    const topCategories = Object.entries(categoryTotals)
      .map(([category, totals]) => ({
        category,
        total: totals.income - totals.expenses,
        income: totals.income,
        expenses: totals.expenses
      }))
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
      .slice(0, 5);

    setSummary({
      totalIncome,
      totalExpenses,
      netCashFlow,
      paymentIncome,
      paymentExpenses,
      operationalIncome,
      operationalExpenses,
      transactionsByType,
      cashFlowByHour,
      topCategories
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...allTransactions];

    // Filtrar por tipo de transacción
    if (transactionType !== 'todos') {
      filtered = filtered.filter(transaction =>
        transaction.type === transactionType
      );
    }

    // Filtrar por término de búsqueda
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(transaction =>
        transaction.invoiceNumber?.toString().toLowerCase().includes(searchTerm) ||
        transaction.clientName?.toLowerCase().includes(searchTerm) ||
        transaction.description?.toLowerCase().includes(searchTerm) ||
        transaction.category_name?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredTransactions(filtered);
    calculateSummary(filtered);
  }, [allTransactions, search, transactionType]);

  const prepareChartData = () => {
    // Datos de flujo de caja por hora
    const hourlyData = Object.values(summary.cashFlowByHour)
      .sort((a, b) => a.hour.localeCompare(b.hour));

    // Datos por tipo de transacción
    const typeData = Object.entries(summary.transactionsByType).map(([type, data]) => ({
      type: type === 'payment' ? 'Facturación' : 'Operativo',
      amount: data.amount,
      count: data.count,
      color: type === 'payment' ? COLORS[0] : COLORS[1]
    }));

    // Datos de categorías
    const categoryData = summary.topCategories.map((cat, index) => ({
      category: cat.category,
      amount: cat.total,
      color: COLORS[index % COLORS.length]
    }));

    return {
      hourly: hourlyData,
      types: typeData,
      categories: categoryData
    };
  };

  useEffect(() => {
    if (!permissionError) {
      loadAllData();
    }
  }, [permissionError, selectedDate]);

  useEffect(() => {
    if (allTransactions.length > 0) {
      applyFilters();
    }
  }, [search, transactionType, allTransactions, applyFilters]);

  const chartData = prepareChartData();

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev =>
      isExpanded
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    );
  };

  if (loading) return <LoadingInformation />;

  // Componente para mostrar cuando no hay datos
  const NoDataComponent = () => (
    <Card sx={{
      borderRadius: 3,
      p: 4,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      border: '2px solid #f59e0b'
    }}>
      <ErrorOutline sx={{ fontSize: 80, color: '#f59e0b', mb: 2 }} />
      <Typography variant="h5" sx={{ mb: 2, color: '#92400e', fontWeight: 600 }}>
        No hay movimientos de caja hoy
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: '#78350f' }}>
        No se encontraron movimientos de caja registrados el día de hoy ({selectedDate.format('DD/MM/YYYY')}). 
        Los datos se actualizarán automáticamente cuando se registren nuevos movimientos.
      </Typography>
      <Button
        variant="contained"
        startIcon={<Refresh />}
        onClick={loadAllData}
        sx={{
          backgroundColor: '#f59e0b',
          '&:hover': { backgroundColor: '#d97706' },
          px: 4,
          py: 1
        }}
      >
        Actualizar Datos
      </Button>
    </Card>
  );

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <div style={{ background: '#f3e8ff', minHeight: '100vh', padding: '20px 0' }}>
      <Container maxWidth="xl">
        {contextHolder}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              color: '#4b0082',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
              fontSize: {
                xs: '1.5rem',
                sm: '2.125rem',
                md: '3rem'
              }
            }}
          >
            <AccountBalanceWallet sx={{ color: '#f759ab', fontSize: { xs: 30, sm: 50 } }} />
            Reporte de Caja Diario - {selectedDate.format('DD/MM/YYYY')}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ChevronLeft />}
              onClick={goToPreviousDay}
              variant="outlined"
              sx={{ borderColor: '#f759ab', color: '#f759ab', '&:hover': { borderColor: '#e04897', backgroundColor: '#fce7f3' } }}
            >
              Día Anterior
            </Button>
            <Button
              startIcon={<TodayIcon />}
              onClick={goToToday}
              variant="outlined"
              sx={{ borderColor: '#f759ab', color: '#f759ab', '&:hover': { borderColor: '#e04897', backgroundColor: '#fce7f3' } }}
            >
              Hoy
            </Button>
            <Button
              startIcon={<ChevronRight />}
              onClick={goToNextDay}
              variant="outlined"
              sx={{ borderColor: '#f759ab', color: '#f759ab', '&:hover': { borderColor: '#e04897', backgroundColor: '#fce7f3' } }}
              disabled={selectedDate.isSame(moment(), 'day')}
            >
              Día Siguiente
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={loadAllData}
              variant="outlined"
              sx={{ borderColor: '#f759ab', color: '#f759ab', '&:hover': { borderColor: '#e04897', backgroundColor: '#fce7f3' } }}
            >
              Actualizar
            </Button>
          </Stack>
        </Box>

        {/* Mostrar mensaje si no hay datos */}
        {allTransactions.length === 0 ? (
          <NoDataComponent />
        ) : (
          <>
            {/* Filters */}
            <Card sx={{ mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: '#4b0082',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FilterList sx={{ mr: 1, color: '#f759ab' }} />
                  Filtros de Búsqueda
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                      <Input
                        placeholder="Buscar por referencia, cliente, descripción o categoría"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        size="large"
                        prefix={<Search />}
                      />
                    </ConfigProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: '#4b0082' }}>Tipo de transacción</InputLabel>
                      <Select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                        label="Tipo de transacción"
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                        }}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="payment">Movimientos de Facturas</MenuItem>
                        <MenuItem value="operational">Transacciones Operativas</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  borderRadius: 3,
                  border: '2px solid #22c55e',
                  background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d' }}>
                          {formatCurrency(summary.totalIncome)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Ingresos
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 40, color: '#22c55e' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  borderRadius: 3,
                  border: '2px solid #ef4444',
                  background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#dc2626' }}>
                          {formatCurrency(summary.totalExpenses)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Egresos
                        </Typography>
                      </Box>
                      <TrendingDown sx={{ fontSize: 40, color: '#ef4444' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  borderRadius: 3,
                  border: summary.netCashFlow >= 0 ? '2px solid #22c55e' : '2px solid #ef4444',
                  background: summary.netCashFlow >= 0
                    ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)'
                    : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{
                          fontWeight: 700,
                          color: summary.netCashFlow >= 0 ? '#15803d' : '#dc2626'
                        }}>
                          {formatCurrency(Math.abs(summary.netCashFlow))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Flujo Neto ({summary.netCashFlow >= 0 ? '+' : '-'})
                        </Typography>
                      </Box>
                      <AccountBalance sx={{
                        fontSize: 40,
                        color: summary.netCashFlow >= 0 ? '#22c55e' : '#ef4444'
                      }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, border: '2px solid #8b5cf6', background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#6b21a8' }}>
                          {filteredTransactions.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Transacciones
                        </Typography>
                      </Box>
                      <Receipt sx={{ fontSize: 40, color: '#8b5cf6' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                    Flujo de Caja por Hora
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData.hourly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name === 'income' ? 'Ingresos' : name === 'expenses' ? 'Egresos' : 'Flujo Neto'
                        ]}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="income"
                        stackId="1"
                        stroke="#22c55e"
                        fill="#22c55e"
                        fillOpacity={0.6}
                        name="Ingresos"
                      />
                      <Area
                        type="monotone"
                        dataKey="expenses"
                        stackId="2"
                        stroke="#ef4444"
                        fill="#ef4444"
                        fillOpacity={0.6}
                        name="Egresos"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                    Distribución por Tipo
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.types}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ type, percent }) => `${type}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {chartData.types.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Leyenda personalizada */}
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    borderTop: '1px solid #e0e7ff',
                    pt: 2
                  }}>
                    {chartData.types.map((entry, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: entry.color,
                              borderRadius: '50%'
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {entry.type} ({entry.count})
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: '#4b0082',
                          fontSize: '0.85rem'
                        }}>
                          {formatCurrency(entry.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Breakdown */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                      Desglose Detallado
                    </Typography>

                    {/* Payments Section */}
                    <Accordion
                      expanded={expandedPanels.includes('payments')}
                      onChange={handlePanelChange('payments')}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Payment sx={{ color: '#f759ab' }} />
                          <Typography variant="h6" sx={{ color: '#4b0082', fontWeight: 600 }}>
                            Movimientos de Facturas ({payments.length} registros)
                          </Typography>
                          <Chip
                            label={formatCurrency(payments.reduce((sum, p) => {
                              const amount = parseNumber(p.amount);
                              return p.flow_type === 'ingreso' ? sum + amount : sum - amount;
                            }, 0))}
                            color={payments.reduce((sum, p) => {
                              const amount = parseNumber(p.amount);
                              return p.flow_type === 'ingreso' ? sum + amount : sum - amount;
                            }, 0) >= 0 ? "success" : "error"}
                            size="small"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Factura</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Cliente</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Método</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Monto</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Descripción</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Acciones</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payments
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((payment, index) => (
                                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                                    <TableCell>
                                      <Chip
                                        label={payment.flow_type.toUpperCase()}
                                        color={payment.flow_type === 'ingreso' ? 'success' : 'error'}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {payment.creationDate
                                        ? moment(payment.creationDate).format('DD/MM/YYYY HH:mm A')
                                        : 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                                      {payment.invoiceNumber}
                                    </TableCell>
                                    <TableCell>{payment.clientName}</TableCell>
                                    <TableCell>
                                      <Chip
                                        icon={payment.paymentMethod === 'efectivo' ? <AttachMoney /> :
                                          payment.paymentMethod === 'Tarjeta' ? <CreditCard /> :
                                            <AccountBalance />}
                                        label={payment.paymentMethod?.toUpperCase() || 'EFECTIVO'}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          color: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                            payment.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316',
                                          borderColor: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                            payment.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{
                                      fontWeight: 600,
                                      color: payment.flow_type === 'ingreso' ? '#22c55e' : '#ef4444'
                                    }}>
                                      {payment.flow_type === 'ingreso' ? '+' : '-'}{formatCurrency(payment.amount)}
                                    </TableCell>
                                    <TableCell>
                                      {payment.description || '—'}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title="Ver factura">
                                        <IconButton
                                          onClick={() => navigate(`/billing/invoice/${payment.invoiceID}`)}
                                          sx={{ color: '#f759ab' }}
                                        >
                                          <Visibility />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>

                    {/* Operational Transactions Section */}
                    <Accordion
                      expanded={expandedPanels.includes('operational')}
                      onChange={handlePanelChange('operational')}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Business sx={{ color: '#f759ab' }} />
                          <Typography variant="h6" sx={{ color: '#4b0082', fontWeight: 600 }}>
                            Transacciones Operativas ({operationalTransactions.length} registros)
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={`+${formatCurrency(operationalTransactions.filter(t => t.flow_type === 'ingreso').reduce((sum, t) => sum + parseNumber(t.amount), 0))}`}
                              color="success"
                              size="small"
                            />
                            <Chip
                              label={`-${formatCurrency(operationalTransactions.filter(t => t.flow_type === 'egreso').reduce((sum, t) => sum + parseNumber(t.amount), 0))}`}
                              color="error"
                              size="small"
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Referencia</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Categoría</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Método</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Monto</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Descripción</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {operationalTransactions
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((transaction, index) => (
                                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                                    <TableCell>
                                      {transaction.transaction_date
                                        ? moment(transaction.transaction_date).format('DD/MM/YYYY HH:mm A')
                                        : 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                                      {transaction.receipt_reference || `OP-${transaction.transaction_ID}`}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={transaction.flow_type.toUpperCase()}
                                        color={transaction.flow_type === 'ingreso' ? 'success' : 'error'}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>{transaction.category_name}</TableCell>
                                    <TableCell>
                                      <Chip
                                        icon={transaction.paymentMethod === 'efectivo' ? <AttachMoney /> :
                                          transaction.paymentMethod === 'Tarjeta' ? <CreditCard /> :
                                            <AccountBalance />}
                                        label={transaction.paymentMethod?.toUpperCase() || 'EFECTIVO'}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          color: transaction.paymentMethod === 'efectivo' ? '#22c55e' :
                                            transaction.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316',
                                          borderColor: transaction.paymentMethod === 'efectivo' ? '#22c55e' :
                                            transaction.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{
                                      fontWeight: 600,
                                      color: transaction.flow_type === 'ingreso' ? '#22c55e' : '#ef4444'
                                    }}>
                                      {transaction.flow_type === 'ingreso' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                    </TableCell>
                                    <TableCell>
                                      {transaction.description || '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Consolidated Summary */}
            <Card sx={{ borderRadius: 3, mb: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Resumen Consolidado del Día
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ color: '#0369a1', mb: 2 }}>
                        Ingresos del Día
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Pagos de Facturas:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#22c55e' }}>
                            {formatCurrency(summary.paymentIncome)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Ingresos Operativos:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#22c55e' }}>
                            {formatCurrency(summary.operationalIncome)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Total Ingresos:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#22c55e' }}>
                            {formatCurrency(summary.totalIncome)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ color: '#dc2626', mb: 2 }}>
                        Egresos del Día
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Egresos Operativos:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                            {formatCurrency(summary.operationalExpenses)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Egresos de Facturación:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                            {formatCurrency(summary.paymentExpenses)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Total Egresos:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#ef4444' }}>
                            {formatCurrency(summary.totalExpenses)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{
                    p: 3,
                    bgcolor: summary.netCashFlow >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: 3,
                    border: `2px solid ${summary.netCashFlow >= 0 ? '#22c55e' : '#ef4444'}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{
                      color: summary.netCashFlow >= 0 ? '#15803d' : '#dc2626',
                      fontWeight: 700,
                      mb: 1
                    }}>
                      {summary.netCashFlow >= 0 ? '+' : ''}{formatCurrency(summary.netCashFlow)}
                    </Typography>
                    <Typography variant="h6" sx={{
                      color: summary.netCashFlow >= 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 600
                    }}>
                      {summary.netCashFlow >= 0 ? 'FLUJO POSITIVO' : 'FLUJO NEGATIVO'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Balance neto del día
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
}

export default DailyCashReport;
